'use client';
// eslint-disable-next-line deprecate/import
import Link from 'next/link';
import { notFound, useParams } from 'next/navigation';

import { useEffect } from 'react';

import { format } from 'date-fns';
import DOMPurify from 'isomorphic-dompurify';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import ImageProxy from '@next-image-proxy';

import type { Blog } from '@api';

import { useAuth } from '@shared/common/providers/AuthProvider';

import { useBlogArticleQuery, useMoreInterestingBlogArticleQuery } from '@query/blog';

import { LinkeAndShare } from '@msq/components/pages/BlogsPage/BlogCard';
import { BlogCard } from '@msq/components/pages/BlogsPage/BlogCard';
import { ArrowLeft } from '@ui/uikit/components/icons/mono/ArrowLeft';
import { ArrowRight } from '@ui/uikit/components/icons/mono/ArrowRight';

const jsonLdString = (article?: Blog) => {
  if (!article) {
    return '';
  }

  const regex = /(<([^>]+)>)/gi;
  const textContent = article.content.replace(regex, '');
  const cutedText = textContent.split('.')[0] ? textContent.split('.')[0] : textContent;

  const publicHost = process.env.NEXT_PUBLIC_HOST || 'https://magicsquare.io';
  const blogUrl = `${publicHost}/blog/${article.slug}`;

  const defaultJson = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    alternativeHeadline: article.metaDescription,
    image: article.image?.url,
    author: {
      '@type': 'Organization',
      name: 'Magic Square',
      url: 'https://magicsquare.io/',
    },
    datePublished: new Date(article.publishedAt),
    publisher: {
      '@type': 'Organization',
      name: 'Magic Square',
      logo: {
        '@type': 'ImageObject',
        url: 'https://magicsquare.io/favicon-512x512.png',
      },
    },
    description: cutedText,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': blogUrl,
    },
    inLanguage: 'en', // Language of the content
    url: blogUrl,
  };

  if (!article.json) {
    return defaultJson;
  }

  try {
    const jsonString = JSON.stringify(article.json);
    return jsonString;
  } catch (error) {
    return defaultJson;
  }
};

const Page = ({ isAuthServe }: { isAuthServe: boolean }) => {
  const { user } = useAuth();

  const { slug = '' } = useParams<{ slug: string }>();
  const {
    data: articleData,
    isPending: isLoadingArticle,
    refetch: refetchArticle,
  } = useBlogArticleQuery(slug, !!user?.attributes?.uuid || isAuthServe, user?.attributes?.uuid);
  const { data: interestingArticles, refetch: refetchInterestingArticles } =
    useMoreInterestingBlogArticleQuery(
      slug,
      !!user?.attributes?.uuid || isAuthServe,
      user?.attributes?.uuid,
    );

  const article = articleData?.data;

  const jsonLd = jsonLdString(article);

  useEffect(() => {
    // for get info about user has like already need refetch data
    if (user?.attributes?.uuid) {
      refetchArticle();
      refetchInterestingArticles();
    }
  }, [user, refetchArticle, refetchInterestingArticles]);

  if (!article && !isLoadingArticle) {
    return notFound();
  }

  return (
    <div className="v2-container">
      <div className="max-w-4xl m-auto">
        <div className="py-10">
          <ul className="flex justify-start gap-2">
            <li>
              <Link href="/blog" className="text-caption-xs-a text-base-text-tertiary">
                Magic Blog
              </Link>
            </li>
            <li>
              <ArrowRight className="icon-2xs text-caption-xs-a text-base-text-tertiary" />
            </li>
            <li>
              <span className="text-caption-xs-a text-base-text-tertiary">{article?.title}</span>
            </li>
          </ul>
          <h1 className="text-h1-a pb-8">{article?.title}</h1>
          {article && (
            <>
              <div className="flex flex-wrap justify-start items-center gap-6">
                <LinkeAndShare article={article} updatingKey="blogArticle" className="gap-6" />
                <div className="inline-block w-auto py-2 px-3 rounded-full bg-base-bg-secondary text-base-text-primary text-caption-sm-a whitespace-nowrap">
                  {article?.category ? article.category.name : ''}
                </div>
                <span className="text-caption-sm-a text-base-text-tertiary">
                  {format(new Date(article.createdAt), 'dd MMM yyyy')}
                </span>
              </div>
              {article?.tags && !!article?.tags?.length && (
                <div className="flex flex-wrap gap-[6px] pt-6">
                  {article?.tags.map((tag) => (
                    <Link
                      href={`/blog/tag/${tag.slug}`}
                      key={tag.documentId}
                      className="inline-block w-auto px-2 py-1 rounded-full bg-base-bg-secondary hover:bg-button-secondary-hover-bg transition-all text-base-text-primary text-caption-xs-a whitespace-nowrap"
                    >
                      {tag.name}
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <div className="pb-14">
          <ImageProxy
            width={1920}
            height={1080}
            quality={100}
            src={article?.image?.url}
            className="w-full h-auto rounded-3xl select-none"
            alt={article?.imageAlt || article?.title || 'magic square blog'}
          />
        </div>
        <div
          className="blog-content"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article?.content as string) }}
        />
      </div>
      <div className="pt-20">
        <h2 className="text-h2-a text-base-text-primary pb-8">More Interesting for You</h2>
        <div className="relative arrows-control-wrapper">
          <Swiper
            className="w-full relative"
            slidesPerView="auto"
            modules={[Pagination, Navigation]}
            pagination={{
              el: '.pagination-recommended-articles',
              type: 'bullets',
              clickable: true,
              bulletClass: 'pagination--block',
              bulletActiveClass: 'pagination--active',
              renderBullet: (_, className) => {
                return `<div class="${className} w-6 h-6 flex justify-center items-center"><span class="pagination--item"></span></div>`;
              },
            }}
            navigation={{
              prevEl: '.prev-button',
              nextEl: '.next-button',
            }}
          >
            <div className="pagination-recommended-articles w-full flex justify-center items-center gap-2 mt-4"></div>

            {interestingArticles?.data.map((interestingArticle) => {
              return (
                <SwiperSlide key={interestingArticle.id} className="min-w-[332px] w-1/4 px-2">
                  <BlogCard article={interestingArticle} updatingKey="moreInterestingArticles" />
                </SwiperSlide>
              );
            })}
          </Swiper>
          <div>
            <div className="prev-button arrow-control absolute -left-3 t-0 top-[calc(50%_-_12px)]">
              <ArrowLeft className="icon-[24px]" />
            </div>
            <div className="next-button arrow-control absolute -right-3 top-[calc(50%_-_12px)]">
              <ArrowRight className="icon-[24px]" />
            </div>
          </div>
        </div>
      </div>
      {!!jsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}
    </div>
  );
};

export default Page;
